<template>
  <div class="admin-fee-config-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('adminFeeConfig.list.title')" :subtitle="$t('adminFeeConfig.page.subtitle')" />

    <div class="admin-fee-config-layout">
      <!-- 左侧费用类型树 -->
      <div class="ds-panel ds-panel--tree">
        <community-fee-type-tree ref="communityFeeTypeTree" @selectCommunity="handleSelectCommunity"
          @selectFeeTypeCd="handleSelectFeeTypeCd" @selectFeeFlag="handleSelectFeeFlag" />
      </div>

      <div class="admin-fee-config-main">
        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="searchForm.configId" clearable class="w-full"
                :placeholder="$t('adminFeeConfig.search.configIdPlaceholder')" @keyup.enter.native="handleSearch" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="searchForm.feeName" clearable class="w-full"
                :placeholder="$t('adminFeeConfig.search.feeNamePlaceholder')" @keyup.enter.native="handleSearch" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="searchForm.feeFlag" clearable class="w-full"
                :placeholder="$t('adminFeeConfig.search.feeFlagPlaceholder')">
                <el-option v-for="item in feeFlags" :key="item.statusCd" :label="item.name" :value="item.statusCd" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="searchForm.paymentCd" clearable class="w-full"
                :placeholder="$t('adminFeeConfig.search.paymentCdPlaceholder')">
                <el-option v-for="item in paymentCds" :key="item.statusCd" :label="item.name" :value="item.statusCd" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="searchForm.deductFrom" clearable class="w-full"
                :placeholder="$t('adminFeeConfig.search.deductFromPlaceholder')">
                <el-option :label="$t('common.yes')" value="Y" />
                <el-option :label="$t('common.no')" value="N" />
              </el-select>
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">
              {{ $t('common.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="handleReset">
              {{ $t('common.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="getList">
            <template #left>
              <span class="ds-panel__title">{{ $t('adminFeeConfig.list.title') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('adminFeeConfig.page.empty')" @retry="getList">
            <el-table-column prop="configId" :label="$t('adminFeeConfig.table.configId')" align="center" width="140">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.configId || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="feeTypeCdName" :label="$t('adminFeeConfig.table.feeTypeCd')" align="center" width="130">
              <template slot-scope="scope">
                {{ scope.row.feeTypeCdName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="feeName" :label="$t('adminFeeConfig.table.feeName')" align="left" min-width="150"
              show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.feeName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="feeFlagName" :label="$t('adminFeeConfig.table.feeFlag')" align="center" width="130">
              <template slot-scope="scope">
                {{ scope.row.feeFlagName || '-' }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminFeeConfig.table.paymentCd')" align="center" width="110">
              <template slot-scope="scope">
                {{ scope.row.paymentCd === '1200' ? $t('adminFeeConfig.page.prepaid') : $t('adminFeeConfig.page.postpaid') }}
              </template>
            </el-table-column>
            <el-table-column prop="paymentCycle" :label="$t('adminFeeConfig.table.paymentCycle')" align="center" width="110">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.paymentCycle || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="computingFormulaName" :label="$t('adminFeeConfig.table.computingFormula')" align="center"
              min-width="140" show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.computingFormulaName || '-' }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminFeeConfig.table.squarePrice')" align="right" width="120">
              <template slot-scope="scope">
                <span v-if="scope.row.computingFormula === '2002'" class="ds-text-muted">-</span>
                <span v-else class="ds-num">{{ formatMoney(scope.row.squarePrice) }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminFeeConfig.table.additionalAmount')" align="right" width="130">
              <template slot-scope="scope">
                <span class="ds-num">{{ formatMoney(scope.row.additionalAmount) }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminFeeConfig.table.deductFrom')" align="center" width="100">
              <template slot-scope="scope">
                <ds-status-tag :type="scope.row.deductFrom === 'Y' ? 'success' : 'muted'"
                  :label="scope.row.deductFrom === 'Y' ? $t('common.yes') : $t('common.no')" />
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminFeeConfig.table.state')" align="center" width="100">
              <template slot-scope="scope">
                <ds-status-tag :type="scope.row.state === 'Y' ? 'success' : 'muted'"
                  :label="scope.row.state === 'Y' ? $t('common.enabled') : $t('common.disabled')" />
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
            :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
            <template #summary>
              <div class="ds-tip-text">
                <p>{{ $t('adminFeeConfig.description.feeFlag') }}</p>
                <p>{{ $t('adminFeeConfig.description.oneTimeFee') }}</p>
                <p>{{ $t('adminFeeConfig.description.paymentType') }}</p>
              </div>
            </template>
          </ds-pagination-bar>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { queryAdminFeeConfigs } from '@/api/fee/adminFeeConfigApi'
import CommunityFeeTypeTree from '@/components/fee/CommunityFeeTypeTree'
import { getDict } from '@/api/community/communityApi'
import { formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'AdminFeeConfigList',
  components: {
    CommunityFeeTypeTree,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        configId: '',
        feeName: '',
        feeFlag: '',
        paymentCd: '',
        deductFrom: '',
        feeTypeCd: '',
        communityId: ''
      },
      tableData: [],
      feeFlags: [],
      paymentCds: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.getDictData()

    this.getList()
    setTimeout(() => {
      this.$refs.communityFeeTypeTree.open()
    }, 1000)
  },
  methods: {
    async getList() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          ...this.searchForm,
          page: this.pagination.current,
          row: this.pagination.size
        }
        const { feeConfigs, total } = await queryAdminFeeConfigs(params)
        this.tableData = Array.isArray(feeConfigs) ? feeConfigs : []
        this.pagination.total = total || 0
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('adminFeeConfig.page.loadFailed')
        this.$message.error(this.$t('adminFeeConfig.fetchError'))
      } finally {
        this.loading = false
      }
    },
    async getDictData() {
      try {
        // 这里应该调用获取字典数据的API
        this.feeFlags = await getDict('pay_fee_config', 'fee_flag')
        this.paymentCds = await getDict('pay_fee_config', 'payment_cd')
      } catch (error) {
        console.error('Failed to load dictionary data:', error)
      }
    },
    handleSearch() {
      this.pagination.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm = {
        configId: '',
        feeName: '',
        feeFlag: '',
        paymentCd: '',
        deductFrom: '',
        feeTypeCd: '',
        communityId: ''
      }
      this.handleSearch()
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.getList()
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this.getList()
    },
    handleSelectCommunity({ communityId }) {
      this.searchForm.communityId = communityId
      this.searchForm.feeTypeCd = ''
      this.searchForm.feeFlag = ''
      this.handleSearch()
    },
    handleSelectFeeTypeCd({ feeTypeCd, communityId }) {
      this.searchForm.communityId = communityId
      this.searchForm.feeTypeCd = feeTypeCd
      this.searchForm.feeFlag = ''
      this.handleSearch()
    },
    handleSelectFeeFlag({ feeFlag, feeTypeCd, communityId }) {
      this.searchForm.communityId = communityId
      this.searchForm.feeTypeCd = feeTypeCd
      this.searchForm.feeFlag = feeFlag
      this.handleSearch()
    },
    formatMoney(value) {
      return formatMoney(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.admin-fee-config-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 左侧费用类型树 + 右侧主内容 */
.admin-fee-config-layout {
  display: flex;
  gap: var(--ds-spacing-md);
  align-items: flex-start;
}

.admin-fee-config-main {
  flex: 1 1 auto;
  min-width: 0;
}

/* 费用类型树面板 */
.ds-panel--tree {
  width: 280px;
  flex-shrink: 0;
  overflow: auto;
  max-height: calc(100vh - 220px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;

  p {
    margin: 0;
  }
}

/* 辅助文本 */
.ds-text-muted {
  color: var(--ds-gray-500);
}
</style>
